import { Box, Grid as MuiGrid } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { memo, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { GridContainer } from '~/components/GridContainer';
import { Typography } from '~/components/Typography';
import { EmptyState } from '~/components/empty-state';
import UploadMediaIllustration from '~/images/illustrations/upload-media.svg';
import type { ContentCardProps } from './ContentCard';
import type { ContentFolderCardProps } from './ContentFolderCard';
import { ContentGridRow } from './ContentGridRow';

export interface ContentGridProps {
  items: readonly ContentCardProps['item'][];
  folders: readonly ContentFolderCardProps['contentFolder'][];
  loading?: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}
export const ContentGrid = memo(
  ({ items, folders, loading, onCheck, selectedIds }: ContentGridProps) => {
    const { t } = useTranslation();

    return (
      <GridContainer
        loading={loading}
        isEmpty={folders.length === 0 && items.length === 0}
        emptyState={
          <EmptyState
            illustration={UploadMediaIllustration}
            header={t('pages.content.empty_state.header')}
            description={t('pages.content.empty_state.description')}
          />
        }
      >
        {folders.length > 0 && (
          <Box
            sx={{
              paddingBottom: 4,
              marginBottom: 4,
              borderBottom: items.length > 0 ? 1 : 0,
              borderBottomColor: 'borders.main',
            }}
          >
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              {t('pages.content.grid.folders')} ({folders.length})
            </Typography>
            <MuiGrid container spacing={2}>
              {folders.map((folder) => (
                <ContentGridRow
                  key={`ContentFolder-${folder.id}`}
                  item={folder}
                  onCheck={onCheck}
                  selectedIds={selectedIds}
                />
              ))}
            </MuiGrid>
          </Box>
        )}
        {items.length > 0 && (
          <Box sx={{ paddingBottom: 4 }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              {t('pages.content.grid.items')} ({items.length})
            </Typography>
            <MuiGrid container spacing={2}>
              {items.map((item) => (
                <ContentGridRow
                  key={`${item.__typename}-${item.id}`}
                  item={item}
                  onCheck={onCheck}
                  selectedIds={selectedIds}
                />
              ))}
            </MuiGrid>
          </Box>
        )}
      </GridContainer>
    );
  },
);

ContentGrid.displayName = 'ContentGrid';
